import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, Mail, Building2, User, Shield } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = 'Activate account · USKE Procurement Portal';
    if (!token) { setLoading(false); setError('Missing invitation token.'); return; }
    axios.get(`${BACKEND_URL}/api/invitations/${token}`)
      .then((res) => setInvite(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'This invitation is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return toast({ title: 'Password too short', description: 'Minimum 8 characters.', variant: 'destructive' });
    if (newPassword !== confirmPassword) return toast({ title: 'Passwords don\'t match', variant: 'destructive' });
    if (!acceptTerms) return toast({ title: 'Please accept the terms', variant: 'destructive' });
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/invitations/accept`, { token, new_password: newPassword, accept_terms: true });
      setDone(true);
      toast({ title: 'Account activated', description: 'You can now sign in.' });
      setTimeout(() => navigate('/portal/login'), 2000);
    } catch (err) {
      toast({ title: 'Activation failed', description: err.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="accept-invitation-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="inline-block bg-blue-900 text-white p-3 rounded font-bold text-sm mb-2">USKE Procurement Portal</div>
          <p className="text-xs text-slate-500">USKE SUPPLY LLC · Washington State Contract No. 09925</p>
        </div>
        <Card className="border-2">
          <CardContent className="p-6">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-10 w-10 animate-spin text-blue-700" /></div>
            ) : error ? (
              <div className="text-center py-8">
                <XCircle className="h-12 w-12 text-rose-500 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-slate-800">Invitation unavailable</h2>
                <p className="text-sm text-slate-500 mt-2">{error}</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/portal/login')} data-testid="go-login">Go to sign-in</Button>
              </div>
            ) : done ? (
              <div className="text-center py-8" data-testid="activation-success">
                <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-slate-800">Account activated</h2>
                <p className="text-sm text-slate-500 mt-2">Redirecting you to sign-in…</p>
              </div>
            ) : invite?.status === 'expired' ? (
              <div className="text-center py-8">
                <XCircle className="h-12 w-12 text-orange-500 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-slate-800">This invitation has expired</h2>
                <p className="text-sm text-slate-500 mt-2">Please ask <strong>{invite.invited_by_name}</strong> to resend it from the portal.</p>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold text-slate-800 mb-1">Activate your account</h2>
                <p className="text-sm text-slate-500 mb-4">Welcome to the USKE Procurement Portal. Set your password and accept the terms to finish.</p>
                <div className="bg-slate-50 border rounded p-3 space-y-1 text-xs mb-4">
                  <div className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-slate-400" /> <strong>{invite?.agency_name}</strong></div>
                  <div className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-slate-400" /> {invite?.user_name} <span className="text-[10px] bg-blue-100 text-blue-700 px-1 rounded uppercase">{invite?.role?.replace('_', ' ')}</span></div>
                  <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" /> {invite?.user_email}</div>
                  <div className="text-[10px] text-slate-400">Invited by {invite?.invited_by_name}</div>
                </div>
                <form onSubmit={submit} className="space-y-3">
                  <div>
                    <Label>New password *</Label>
                    <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} placeholder="At least 8 characters" data-testid="accept-new-password" />
                  </div>
                  <div>
                    <Label>Confirm password *</Label>
                    <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="accept-confirm-password" />
                  </div>
                  <label className="flex items-start gap-2 text-xs text-slate-600 pt-1">
                    <Checkbox checked={acceptTerms} onCheckedChange={setAcceptTerms} className="mt-0.5" data-testid="accept-terms-checkbox" />
                    <span>I accept the <a href="/terms" className="text-blue-700 underline" target="_blank" rel="noreferrer">Terms of Use</a> and <a href="/privacy" className="text-blue-700 underline" target="_blank" rel="noreferrer">Privacy Policy</a>.</span>
                  </label>
                  <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-blue-50 border border-blue-100 rounded p-2">
                    <Shield className="h-3.5 w-3.5 text-blue-700 mt-0.5" />
                    We recommend enabling two-factor authentication (TOTP) from <strong>Security</strong> after your first sign-in.
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="accept-submit">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Activate account
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvitation;
